import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Clock, Calendar, Zap } from "lucide-react";
import { ManagedCampaignData } from "./ManagedCampaignData";

interface ManagedScheduleStepProps {
  data: ManagedCampaignData;
  updateData: (updates: Partial<ManagedCampaignData>) => void;
  /** Existing campaign being edited, so its own allocation isn't double-counted. */
  campaignId?: string | null;
}

const TIMEZONES = [
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "America/New_York", label: "US Eastern (EST/EDT)" },
  { value: "America/Los_Angeles", label: "US Pacific (PST/PDT)" },
  { value: "America/Chicago", label: "US Central (CST/CDT)" },
  { value: "Europe/London", label: "UK (GMT/BST)" },
  { value: "Europe/Paris", label: "Central Europe (CET/CEST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Asia/Dubai", label: "UAE (GST)" },
  { value: "Australia/Sydney", label: "Australia Eastern (AEST/AEDT)" },
];

const DAYS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];

export const ManagedScheduleStep = ({ data, updateData, campaignId }: ManagedScheduleStepProps) => {
  const is24x7 = data.workingHoursStart === 0 && 
                 data.workingHoursEnd === 24 && 
                 data.workingDays.length === 7;

  const toggle24x7 = (enabled: boolean) => {
    if (enabled) {
      updateData({
        workingHoursStart: 0,
        workingHoursEnd: 24,
        workingDays: [0, 1, 2, 3, 4, 5, 6],
      });
    } else {
      updateData({
        workingHoursStart: 9,
        workingHoursEnd: 18,
        workingDays: [1, 2, 3, 4, 5],
      });
    }
  };

  const toggleDay = (day: number) => {
    const newDays = data.workingDays.includes(day)
      ? data.workingDays.filter(d => d !== day)
      : [...data.workingDays, day].sort((a, b) => a - b);
    updateData({ workingDays: newDays });
  };

  return (
    <div className="space-y-6">
      {/* Timezone */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-muted-foreground" />
          <Label className="text-sm font-medium">Timezone</Label>
        </div>
        <p className="text-xs text-muted-foreground">
          Campaign will run according to this timezone
        </p>
        <Select
          value={data.timezone}
          onValueChange={(value) => updateData({ timezone: value })}
        >
          <SelectTrigger className="max-w-md">
            <SelectValue placeholder="Select timezone" />
          </SelectTrigger>
          <SelectContent>
            {TIMEZONES.map((tz) => (
              <SelectItem key={tz.value} value={tz.value}>
                {tz.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* 24/7 Toggle */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 max-w-md">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-primary" />
          <div>
            <Label className="text-sm font-medium">Run 24/7</Label>
            <p className="text-xs text-muted-foreground">Send messages anytime, any day</p>
          </div>
        </div>
        <Switch checked={is24x7} onCheckedChange={toggle24x7} />
      </div>

      {/* Working Hours */}
      {!is24x7 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Working Hours</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            Campaign will only run during these hours
          </p>
          <div className="flex items-center gap-3 max-w-md">
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground mb-1 block">Start</Label>
              <Select
                value={data.workingHoursStart.toString()}
                onValueChange={(value) => updateData({ workingHoursStart: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={i.toString()}>
                      {i.toString().padStart(2, "0")}:00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="text-muted-foreground mt-5">to</span>
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground mb-1 block">End</Label>
              <Select
                value={data.workingHoursEnd.toString()}
                onValueChange={(value) => updateData({ workingHoursEnd: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => i + 1).map((hour) => (
                    <SelectItem key={hour} value={hour.toString()}>
                      {hour.toString().padStart(2, "0")}:00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Working Days */}
      {!is24x7 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Working Days</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            Campaign will only run on these days
          </p>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((day) => (
              <button
                key={day.value}
                type="button"
                onClick={() => toggleDay(day.value)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  data.workingDays.includes(day.value)
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {day.label}
              </button>
            ))}
           </div>
         </div>
       )}
     </div>
  );
};
